/**
 * Admin Academy Section - Courses & Training with Course Selection
 * Flow: Select Course Category → View Course-Specific Metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  GraduationCap, Users, DollarSign, TrendingUp, BookOpen, Download,
  Star, Award, Key, CheckCircle, Clock, AlertTriangle,
  Loader2, Eye, ShoppingCart, Percent, UserPlus, ArrowLeft, Zap
} from 'lucide-react';

const AdminAcademySection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Course categories/packages for Academy
  const courses = {
    cdl: {
      name: 'CDL Mastery 101',
      subtitle: 'Master Your CDL',
      icon: Award,
      color: '#D4AF37',
      price: '$250',
      description: 'Complete CDL training and test preparation',
      popular: true,
      stats: {
        enrolled: 89,
        completed: 67,
        completionRate: 75,
        revenue: 22250,
        avgRating: 4.9,
        activeStudents: 22
      },
      modules: [
        { name: 'CDL Requirements Overview', completed: 89 },
        { name: 'Pre-Trip Inspection', completed: 78 },
        { name: 'Basic Vehicle Control', completed: 72 },
        { name: 'Road Test Preparation', completed: 69 },
        { name: 'Endorsements Guide', completed: 67 }
      ],
      recentStudents: [
        { name: 'John Martinez', progress: 100, enrolled: '2026-02-15' },
        { name: 'Sarah Williams', progress: 80, enrolled: '2026-03-01' },
        { name: 'Mike Johnson', progress: 45, enrolled: '2026-03-10' }
      ]
    },
    trucking: {
      name: 'Trucking 101',
      subtitle: 'Start Your Trucking Business',
      icon: CheckCircle,
      color: '#10B981',
      price: '$250',
      description: 'Everything you need to launch your trucking career',
      stats: {
        enrolled: 67,
        completed: 45,
        completionRate: 67,
        revenue: 16750,
        avgRating: 4.8,
        activeStudents: 22
      },
      modules: [
        { name: 'Industry Overview', completed: 67 },
        { name: 'Getting Your Authority', completed: 58 },
        { name: 'Equipment & Financing', completed: 52 },
        { name: 'Insurance Basics', completed: 48 },
        { name: 'Finding Your First Load', completed: 45 }
      ],
      recentStudents: [
        { name: 'Lisa Chen', progress: 100, enrolled: '2026-02-20' },
        { name: 'David Brown', progress: 65, enrolled: '2026-03-05' },
        { name: 'Amy Lee', progress: 30, enrolled: '2026-03-15' }
      ]
    },
    dispatching: {
      name: 'Dispatching 101',
      subtitle: 'Maximize Your Loads',
      icon: TrendingUp,
      color: '#3B82F6',
      price: '$250',
      description: 'Learn professional dispatch strategies and load optimization',
      stats: {
        enrolled: 45,
        completed: 38,
        completionRate: 84,
        revenue: 11250,
        avgRating: 4.9,
        activeStudents: 7
      },
      modules: [
        { name: 'Dispatch Fundamentals', completed: 45 },
        { name: 'Load Board Mastery', completed: 42 },
        { name: 'Rate Negotiation', completed: 40 },
        { name: 'Route Planning', completed: 39 },
        { name: 'Broker Relationships', completed: 38 }
      ],
      recentStudents: [
        { name: 'Tom Garcia', progress: 100, enrolled: '2026-02-01' },
        { name: 'Jennifer Kim', progress: 90, enrolled: '2026-02-25' },
        { name: 'Chris Park', progress: 55, enrolled: '2026-03-08' }
      ]
    },
    freightbroker: {
      name: 'Freight Broker 101',
      subtitle: 'Build Your Brokerage',
      icon: GraduationCap,
      color: '#8B5CF6',
      price: '$250',
      description: 'Complete guide to starting a freight brokerage business',
      stats: {
        enrolled: 33,
        completed: 22,
        completionRate: 67,
        revenue: 8250,
        avgRating: 4.7,
        activeStudents: 11
      },
      modules: [
        { name: 'Brokerage Fundamentals', completed: 33 },
        { name: 'Licensing & Bonds', completed: 28 },
        { name: 'Finding Shippers', completed: 25 },
        { name: 'Carrier Relationships', completed: 23 },
        { name: 'Scaling Your Business', completed: 22 }
      ],
      recentStudents: [
        { name: 'Rachel Adams', progress: 100, enrolled: '2026-01-15' },
        { name: 'Mark Wilson', progress: 75, enrolled: '2026-02-10' },
        { name: 'Emily Davis', progress: 40, enrolled: '2026-03-01' }
      ]
    }
  };

  // Overall stats
  const overallStats = {
    totalEnrolled: 234,
    activeStudents: 62,
    totalRevenue: 58500,
    avgCompletionRate: 73,
    avgRating: 4.83
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Academy" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Course Selection Screen
  if (!selectedCourse) {
    return (
      <AdminLayout 
        title="Academy Management" 
        subtitle="Courses & Training - Select a course to view metrics"
      >
        {/* Overall Summary */}
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Academy Overview</h2>
              <p className="text-white/50 text-sm">{overallStats.totalEnrolled} total students enrolled</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">${(overallStats.totalRevenue / 1000).toFixed(1)}k</p>
                <p className="text-white/40 text-xs">Total Revenue</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">{overallStats.activeStudents}</p>
                <p className="text-white/40 text-xs">Active Students</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{overallStats.avgCompletionRate}%</p>
                <p className="text-white/40 text-xs">Completion Rate</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">{overallStats.avgRating}★</p>
                <p className="text-white/40 text-xs">Avg Rating</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Selection Cards */}
        <h3 className="text-white font-bold mb-4">Select a Course to View Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(courses).map(([key, course]) => (
            <div
              key={key}
              onClick={() => setSelectedCourse(key)}
              className={`bg-[#121212] border p-6 cursor-pointer hover:bg-[#1a1a1a] transition-all relative ${
                course.popular ? 'border-[#D4AF37]' : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`academy-course-${key}`}
            >
              {course.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1">POPULAR</span>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded" style={{ backgroundColor: `${course.color}20` }}>
                  <course.icon className="w-6 h-6" style={{ color: course.color }} />
                </div>
                <div>
                  <p className="text-white font-bold">{course.name}</p>
                  <p className="text-white/50 text-xs">{course.subtitle}</p>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-2xl font-bold" style={{ color: course.color }}>{course.price}</span>
              </div>

              <p className="text-white/50 text-sm mb-4">{course.description}</p>

              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Enrolled</span>
                  <span className="text-white font-bold">{course.stats.enrolled}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Completion Rate</span>
                  <span className="text-[#10B981] font-bold">{course.stats.completionRate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40 text-xs">Rating</span>
                  <span className="text-[#D4AF37] font-bold">{course.stats.avgRating}★</span>
                </div>
              </div>

              <button 
                className="w-full mt-4 py-3 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: `${course.color}20`, 
                  color: course.color,
                  border: `1px solid ${course.color}40`
                }}
              >
                View {course.name} Metrics →
              </button>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // Course-Specific Metrics Screen
  const course = courses[selectedCourse];

  return (
    <AdminLayout 
      title={course.name} 
      subtitle={course.subtitle}
    >
      {/* Back Button */}
      <button
        onClick={() => setSelectedCourse(null)}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        data-testid="academy-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Courses
      </button>

      {/* Course Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ 
        backgroundColor: `${course.color}10`, 
        borderColor: `${course.color}40` 
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${course.color}20` }}>
              <course.icon className="w-8 h-8" style={{ color: course.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{course.name}</h2>
              <p className="text-white/50">{course.price} • {course.stats.enrolled} students enrolled</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: course.color }}>${course.stats.revenue.toLocaleString()}</p>
              <p className="text-white/40 text-xs">Revenue</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#D4AF37]">{course.stats.avgRating}★</p>
              <p className="text-white/40 text-xs">Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Users} label="Total Enrolled" value={course.stats.enrolled} color={course.color} onClick={() => setActiveTab('students')} />
        <StatCard icon={CheckCircle} label="Completed" value={course.stats.completed} color="#10B981" onClick={() => setActiveTab('students')} />
        <StatCard icon={Percent} label="Completion Rate" value={`${course.stats.completionRate}%`} color="#3B82F6" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Users} label="Active Students" value={course.stats.activeStudents} color="#F59E0B" onClick={() => setActiveTab('students')} />
        <StatCard icon={DollarSign} label="Revenue" value={`$${course.stats.revenue.toLocaleString()}`} color="#22C55E" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Star} label="Avg Rating" value={course.stats.avgRating} color="#D4AF37" onClick={() => setActiveTab('overview')} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'modules', label: 'Modules', icon: BookOpen },
          { id: 'students', label: 'Students', icon: Users },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`academy-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? course.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Course Features */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Course Description</h3>
            <p className="text-white/70">{course.description}</p>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-white/50 text-sm">This course includes {course.modules.length} modules</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-white">{course.stats.enrolled}</p>
              <p className="text-white/50 text-xs">Total Enrolled</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#10B981]">{course.stats.completed}</p>
              <p className="text-white/50 text-xs">Completed</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#22C55E]">${course.stats.revenue.toLocaleString()}</p>
              <p className="text-white/50 text-xs">Revenue</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#D4AF37]">{course.stats.avgRating}★</p>
              <p className="text-white/50 text-xs">Avg Rating</p>
            </div>
          </div>
        </div>
      )}

      {/* Modules Tab */}
      {activeTab === 'modules' && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">Course Modules</h3>
          <div className="space-y-3">
            {course.modules.map((module, idx) => {
              const completionPct = Math.round((module.completed / course.stats.enrolled) * 100);
              return (
                <div key={idx} className="p-3 bg-[#0A0A0A] border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${course.color}20`, color: course.color }}>
                        {idx + 1}
                      </span>
                      <span className="text-white">{module.name}</span>
                    </div>
                    <span className="text-white/50 text-sm">{module.completed} completed</span>
                  </div>
                  <div className="h-2 bg-white/10 overflow-hidden ml-11">
                    <div 
                      className="h-full transition-all"
                      style={{ width: `${completionPct}%`, backgroundColor: course.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-white font-bold">Recent Students</h3>
            <span className="text-white/50 text-sm">{course.stats.activeStudents} active</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase p-3">Student</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Enrolled</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Progress</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {course.recentStudents.map((student, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                    <td className="p-3 text-white font-medium">{student.name}</td>
                    <td className="p-3 text-white/50">{student.enrolled}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-white/10 overflow-hidden">
                          <div 
                            className="h-full"
                            style={{ width: `${student.progress}%`, backgroundColor: course.color }}
                          />
                        </div>
                        <span className="text-white/70 text-sm">{student.progress}%</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs ${
                        student.progress === 100 ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {student.progress === 100 ? 'Completed' : 'In Progress'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-[#121212] border border-white/10 p-4 sm:p-5 cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 transition-all"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-white/50 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export default AdminAcademySection;
